// Dates and times on screen, in the viewer's own notation and time zone.
//
// Every date or time the web app shows goes through here (CLAUDE.md § 6). The
// styles themselves live in @onlogic/shared (dateText.ts), so a date reads the
// same on screen as in a Google Chat message or an email the server writes;
// this module supplies the viewer's side of it:
//   - their notation — always English words (the app is English-only), with
//     their browser's regional conventions (displayLocale): "12 Mar 2026,
//     15:05" for a Dutch browser, "Mar 12, 2026, 3:05 PM" for a US one or a
//     Chinese-language one in Taiwan;
//   - their time zone — the device's own, for every instant.
// Both are read once per page load (a device that changes zone shows the new
// one on its next launch): naming the zone, rather than leaving it to Intl's
// default, is what lets @onlogic/shared reuse its formatters.
// Calendar dates that are not instants (an all-day booking, a flight's
// departure date, a hire date) go through formatIsoDateOnly, which never
// shifts them a day. Do not pass a hard-coded locale such as 'en-US', build a
// date from getHours()/getMonth(), or render a raw ISO string.
//
// Every formatter takes an optional locale so tests can pin one.

import {
  englishRegionalLocale,
  firstDayOfWeek,
  formatClockTime,
  formatDateText,
  formatHhMm,
  formatIsoDate,
  uses24HourClock as usesTwentyFourHourClock,
  weekdayNames,
} from '@onlogic/shared';

/** A locale override for tests; `undefined` means the viewer's own. */
export type LocaleArg = string | undefined;

// `null` = not read yet this page load.
let cachedLocale: string | null = null;
let cachedTimeZone: string | undefined | null = null;

/**
 * The locale this viewer's dates are written in: always English, with their
 * browser's regional conventions (nl-NL → en-NL, zh-TW → en-TW). Worked out
 * once per page load; see englishRegionalLocale in @onlogic/shared.
 */
export function displayLocale(): string {
  if (cachedLocale === null) {
    const tags =
      typeof navigator === 'undefined'
        ? []
        : navigator.languages?.length
          ? navigator.languages
          : navigator.language
            ? [navigator.language]
            : [];
    cachedLocale = englishRegionalLocale(tags);
  }
  return cachedLocale;
}

/**
 * The time zone instants are shown in: the device's own. `undefined` only on
 * a runtime that cannot say, which leaves Intl's default (the same zone,
 * without formatter reuse).
 */
export function displayTimeZone(): string | undefined {
  if (cachedTimeZone === null) {
    try {
      cachedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch {
      cachedTimeZone = undefined;
    }
  }
  return cachedTimeZone;
}

/** Test seam: forget the locale and zone so the next call reads them again. */
export function resetDisplayFormatForTests(): void {
  cachedLocale = null;
  cachedTimeZone = null;
}

/** The viewer's notation (or a test's `locale`) in the viewer's zone. */
function viewer(locale: LocaleArg): { locale: LocaleArg; timeZone: string | undefined } {
  return { locale, timeZone: displayTimeZone() };
}

/**
 * A stored instant as a Date: a Firestore Timestamp, a Date, an ISO string or
 * a millisecond count. `undefined` for anything unreadable, so callers can show
 * their own placeholder instead of "Invalid Date".
 */
export function toDate(value: unknown): Date | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
  if (typeof (value as { toDate?: unknown }).toDate === 'function') {
    return toDate((value as { toDate: () => Date }).toDate());
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
}

/** The styles an instant can be shown in (see DateTextStyle in @onlogic/shared). */
export type InstantStyle =
  | 'date'
  | 'dateLong'
  | 'dateTime'
  | 'dateTimeSeconds'
  | 'time'
  | 'weekdayDate'
  | 'weekdayDateTime'
  | 'dayMonth'
  | 'dayMonthTime';

/**
 * Any stored instant (Timestamp, Date, ISO string) in one of the styles, in the
 * viewer's notation and zone — or `placeholder` when there is nothing readable.
 */
export function formatInstant(
  value: unknown,
  style: InstantStyle = 'dateTime',
  placeholder = '—',
  locale: LocaleArg = displayLocale(),
): string {
  const date = toDate(value);
  return date ? formatDateText(date, style, viewer(locale)) : placeholder;
}

/** "12 Mar 2026" · "Mar 12, 2026" */
export function formatDate(date: Date, locale: LocaleArg = displayLocale()): string {
  return formatDateText(date, 'date', viewer(locale));
}

/** "12 March 2026" · "March 12, 2026" */
export function formatDateLong(date: Date, locale: LocaleArg = displayLocale()): string {
  return formatDateText(date, 'dateLong', viewer(locale));
}

/** "12 Mar 2026, 15:05" · "Mar 12, 2026, 3:05 PM" */
export function formatDateTime(date: Date, locale: LocaleArg = displayLocale()): string {
  return formatDateText(date, 'dateTime', viewer(locale));
}

/** "12 Mar 2026, 15:05:09" — for audit and access logs, where seconds matter. */
export function formatDateTimeSeconds(date: Date, locale: LocaleArg = displayLocale()): string {
  return formatDateText(date, 'dateTimeSeconds', viewer(locale));
}

/** "15:05" · "3:05 PM" */
export function formatTime(date: Date, locale: LocaleArg = displayLocale()): string {
  return formatDateText(date, 'time', viewer(locale));
}

/** "Thu 12 Mar" · "Thu, Mar 12" */
export function formatWeekdayDate(date: Date, locale: LocaleArg = displayLocale()): string {
  return formatDateText(date, 'weekdayDate', viewer(locale));
}

/** "Thu 12 Mar, 15:05" · "Thu, Mar 12, 3:05 PM" */
export function formatWeekdayDateTime(date: Date, locale: LocaleArg = displayLocale()): string {
  return formatDateText(date, 'weekdayDateTime', viewer(locale));
}

/** "12 Mar" · "Mar 12" */
export function formatDayMonth(date: Date, locale: LocaleArg = displayLocale()): string {
  return formatDateText(date, 'dayMonth', viewer(locale));
}

/** "12 Mar, 15:05" · "Mar 12, 3:05 PM" */
export function formatDayMonthTime(date: Date, locale: LocaleArg = displayLocale()): string {
  return formatDateText(date, 'dayMonthTime', viewer(locale));
}

/** "March 2026" */
export function formatMonthYearLong(date: Date, locale: LocaleArg = displayLocale()): string {
  return formatDateText(date, 'monthYearLong', viewer(locale));
}

/** "Thursday 12 Mar" · "Thursday, Mar 12" */
export function formatWeekdayDayMonthLong(date: Date, locale: LocaleArg = displayLocale()): string {
  return formatDateText(date, 'weekdayDayMonthLong', viewer(locale));
}

/**
 * A calendar date stored as "YYYY-MM-DD" (or an ISO string whose date part is
 * the one meant), written as that same date whatever the viewer's time zone.
 */
export function formatIsoDateOnly(
  isoDate: string,
  style: 'date' | 'dateLong' | 'weekdayDate' | 'dayMonth' | 'monthYear' | 'monthYearLong' = 'date',
  locale: LocaleArg = displayLocale(),
): string {
  return formatIsoDate(isoDate, style, { locale });
}

/** Whether the viewer's locale writes the clock as 24-hour. */
export function uses24HourClock(locale: LocaleArg = displayLocale()): boolean {
  return usesTwentyFourHourClock(locale);
}

/**
 * An hour of the day (0–23) that is a setting rather than an instant — quiet
 * hours — as a clock time: "22:00" / "07:00" on a 24-hour clock, "10:00 PM" /
 * "7:00 AM" on a 12-hour one. No time zone applies.
 */
export function formatHourOfDay(hour: number, locale: LocaleArg = displayLocale()): string {
  return formatClockTime(hour, 0, { locale });
}

/** A stored 24-hour "HH:MM" wall-clock time (opening hours) in the viewer's clock. */
export function formatHhMmTime(value: string, locale: LocaleArg = displayLocale()): string {
  return formatHhMm(value, { locale });
}

/** The viewer's first day of the week: 1 = Monday … 7 = Sunday. */
export function weekStart(locale: LocaleArg = displayLocale()): number {
  return firstDayOfWeek(locale);
}

/** Weekday names in the viewer's calendar order, starting on their first day. */
export function weekdayLabels(
  width: 'narrow' | 'short' | 'long',
  locale: LocaleArg = displayLocale(),
): string[] {
  return weekdayNames(width, locale);
}
